/**
 * spPacks.ts — the Skill-Point packs a player can BUY (M130, owner-approved
 * 2026-09-22). Pure config: no logic, no imports.
 *
 * THE ONE RULE (owner): bought SP tops up the BANK only. It never touches the
 * CAREER total, so it can never unlock a vehicle, move a skill tier or rank
 * anyone on a leaderboard — see core/skillPoints.ts `spBalance`.
 *
 * WHAT IS AND IS NOT AUTHORITATIVE HERE:
 *   - `id`, `sp`, `usd` are DISPLAY data for the shop's GET SP sheet. The
 *     server owns the real amounts: `sp_packs` in Supabase (seeded by
 *     migration m130_sp_packs_v1.sql — a parity test pins the two in
 *     lockstep) decides how many SP a pack credits, and the price lives in
 *     Stripe / App Store Connect / Play Console, never as a number the client
 *     can assert (the M110 catalog doctrine).
 *   - Packs are sold in USD; Apple and Google localise from that base, and the
 *     web charges USD in phase one.
 *
 * Pricing anchors (the deck): the 9.99 game price, Rocket League's one-cent
 * credit, and the earn rate (a win pays 15 SP). No 0.99 tier on purpose — it
 * would make SP feel cheap.
 */
export interface SpPack {
  /** Stable id — the Stripe/IAP product id namespace AND the `sp_packs` key. */
  id: string;
  /** Marketing name shown on the card. */
  label: string;
  /** Skill Points credited to the BANK. */
  sp: number;
  /** Display price, USD, as the store shows it. */
  usd: string;
  /** Bonus versus the Starter's 50 SP per dollar (0 = none). */
  bonusPct: number;
  /** One line of what it buys, in shop terms. */
  blurb: string;
  /** The card the sheet frames as the pick. */
  featured?: boolean;
  /** M132b (owner 2026-09-26: "start with only the 150 and 400 products for
   *  now"): a pack with no Stripe price yet stays OUT of the GET SP sheet.
   *  The row still exists server-side (the parity test pins it), so lifting
   *  the flag is the whole launch once its Price id lands in `sp_packs`. */
  hidden?: boolean;
}

export const SP_PACKS: readonly SpPack[] = [
  {
    id: 'sp.starter',
    label: 'STARTER',
    sp: 150,
    usd: '2.99',
    bonusPct: 0,
    blurb: 'One kit or wrap',
  },
  {
    id: 'sp.racer',
    label: 'RACER',
    sp: 400,
    usd: '6.99',
    bonusPct: 14,
    blurb: 'Three kits, or a legendary set',
    featured: true,
  },
  {
    id: 'sp.pro',
    label: 'PRO',
    sp: 1000,
    usd: '14.99',
    bonusPct: 33,
    blurb: 'Eight kits, or two legendaries',
    hidden: true, // M132b: no Stripe price yet — flip when the owner adds it
  },
  {
    id: 'sp.champion',
    label: 'CHAMPION',
    sp: 2500,
    usd: '29.99',
    bonusPct: 66,
    blurb: 'Around twenty items',
    hidden: true, // M132b: no Stripe price yet — flip when the owner adds it
  },
];

/** The packs the GET SP sheet actually offers (M132b: the ones with a price). */
export const SP_PACKS_ON_SALE: readonly SpPack[] = SP_PACKS.filter((p) => !p.hidden);

export function spPackById(id: string): SpPack | undefined {
  return SP_PACKS.find((p) => p.id === id);
}

/** Pack ids on the wire / in URLs: `sp.` + a short lowercase token. */
export const SP_PACK_ID_RE = /^sp\.[a-z0-9_]{1,24}$/;
